import React, { useContext } from 'react'
import CssEscape from 'css.escape'

import Link from '@atoms/Link'
import MicrocmsImage from '@atoms/MicrocmsImage'
import SaveButton from '@molecules/SaveButton'
import ShareButton from '@molecules/ShareButton'
import VoicePlayButton from '@molecules/VoicePlayButton'
import MaskedIcon from '@icons/Masked'
import { StoreContext } from '@models/Store'
import { Article } from '@models/Article'
import { convertPlainTitle } from '@models/Parent'
import { useTranslation } from '@models/Translation'

import * as style from './style.module.sass'

type ArticleHeroProps = {
  article: Article
  isMasked: boolean
}
const ArticleHero: React.FC<ArticleHeroProps> = ({ article, isMasked }) => {
  const { locale, t } = useTranslation()

  const Links = () => {
    const requester = article.data.requester
    if (typeof requester === 'string') {
      return <Link to="/request">リクエスト記事</Link>
    }

    const categoryString: string = article.data.category.toUpperCase()
    const categoryLink = (
      <Link to={`/${article.data.category}`}>{categoryString}</Link>
    )

    if (article.parentPage === null) {
      return categoryLink
    }

    const parentTitle =
      article.parentPage.projectName || article.parentPage.title

    return (
      <>
        {categoryLink}
        <span className={style.slash}>/</span>
        <Link to={`/${article.data.category}/${article.parentPage.id}`}>
          {convertPlainTitle(parentTitle, locale)}
        </Link>
      </>
    )
  }

  if (article.data.isSmallHeaderImage) {
    return (
      <div className={style.articleHeaderMini}>
        <div>
          <div className={style.data}>
            {isMasked && (
              <div className={style.limit}>{t('mypage.maskedArticle')}</div>
            )}
            <div className={style.date}>{article.publishDate}</div>
            <div className={style.category}>
              <Links />
            </div>
          </div>
          <h1 className={style.articleTitle}>
            <span dangerouslySetInnerHTML={{ __html: article.titleWithBr }} />
            {isMasked && <MaskedIcon />}
          </h1>
          {article.tags.length > 0 && (
            <Tags tags={article.tags} blogId={article.blogId} />
          )}
          <HeroSns article={article} isSmallHeader />
        </div>
        <div
          className={style.miniImage}
          style={{
            // CssEscape is necessary for image names in Japanese
            backgroundImage: `url(${CssEscape(
              article.data.headerImage.url,
            )}?fm=webp&w=120)`,
          }}
        >
          <MicrocmsImage
            image={article.data.headerImage}
            alt=""
            type="largeHero"
            disableLazyLoad
          />
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={style.articleHeader}>
        <div className={style.data}>
          {isMasked && (
            <div className={style.limit}>{t('mypage.maskedArticle')}</div>
          )}
          <div className={style.date}>{article.publishDate}</div>
          <div className={style.category}>
            <Links />
          </div>
        </div>
        <h1 className={style.articleTitle}>
          <span dangerouslySetInnerHTML={{ __html: article.titleWithBr }} />
          {isMasked && <MaskedIcon />}
        </h1>
        {article.tags.length > 0 && (
          <Tags tags={article.tags} blogId={article.blogId} />
        )}
        <HeroSns article={article} />
      </div>
      <div
        className={style.articleImage}
        style={{
          // CssEscape is necessary for image names in Japanese
          backgroundImage: `url(${CssEscape(
            article.data.headerImage.url,
          )}?fm=webp&w=120)`,
        }}
      >
        <MicrocmsImage
          image={article.data.headerImage}
          alt=""
          type="largeHero"
          disableLazyLoad
        />
      </div>
    </>
  )
}

type TagsProps = {
  tags: string[]
  blogId: string
}
const Tags = ({ tags, blogId }: TagsProps) => {
  return (
    <div className={style.tags}>
      {tags.map(tag => (
        <div
          key={tag}
          data-gtm-click="ui-article-tag"
          data-gtm-label={tag}
          data-gtm-value={blogId}
          className={style.tag}
        >
          <Link to={`/search?q=${encodeURIComponent(tag)}&ref=tag`}>
            #{tag}
          </Link>
        </div>
      ))}
    </div>
  )
}

type HeroSnsProps = {
  article: Article
  isArtisan?: boolean
  isSmallHeader?: boolean
}
export const HeroSns = ({
  article,
  isArtisan = false,
  isSmallHeader = false,
}: HeroSnsProps) => {
  const { entity } = useContext(StoreContext)

  return (
    <div className={style.sns} data-small-header={isSmallHeader}>
      <ShareButton name="x" type="circle" text={article.title} />
      <ShareButton name="linkedin" type="circle" />
      <ShareButton name="facebook" type="circle" />
      <ShareButton name="line" type="circle" />
      <ShareButton name="clipboard" type="circle" />
      <div
        className={style.saveContainer}
        data-gtm-click="save-detail"
        data-gtm-label={entity.bookmark[article.blogId] !== true}
        data-gtm-value={article.blogId}
      >
        <SaveButton
          blogId={article.blogId}
          active={entity.bookmark[article.blogId] === true}
          type="detail"
        />
      </div>
      {article.enableVoice && (
        <div>
          <VoicePlayButton
            article={article}
            type={isArtisan ? 'detail-artisan' : 'detail'}
          />
        </div>
      )}
    </div>
  )
}

export default ArticleHero
